import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { type Analysis, loadAnalysis, type SongRow, titleOf, artistOf } from "../lib/analysis";
import { supabase } from "../lib/supabase";
import { buildRows } from "../components/ChordChart";
import { type ChartMap, parseChart, renderChart, toCorrections } from "../lib/charttext";
import { correctSpan } from "../lib/corrections";
import { PairedEditor, type Focus } from "../components/PairedEditor";
import { type Line, linesFromText, textFromLines } from "../lib/pairededit";

const when = (iso: string) => {
  const minutes = Math.round((Date.now() - Date.parse(iso)) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  return new Date(iso).toLocaleDateString();
};

interface ChartVersion {
  id: number;
  text: string;
  author: string | null;
  created_at: string;
}

/**
 * The chord editor: the chart as plain text.
 *
 * A chord sheet has always been chord names on one line above the word they
 * change on, and that is what gets edited here — not a table of beats, which
 * is how the data happens to be stored rather than how anyone reads a song.
 *
 * Spacing is kept exactly as typed. Saving stores the text verbatim and also
 * reads the chords back out of it, so the chart, the transpose and the play
 * along all follow without anyone maintaining two versions of the truth.
 *
 * Everything here is concert pitch: no transpose, no capo, so a fix means the
 * same thing to whoever reads it in whatever key.
 */
export function SongEditor() {
  const { slug = "" } = useParams();
  const [row, setRow] = useState<SongRow | null>(null);
  const [analysis, setAnalysis] = useState<Analysis | null>(null);
  const [lines, setLines] = useState<Line[]>([]);
  const [focus, setFocus] = useState<Focus>({ line: 0, row: "text", chord: -1, caret: 0 });
  const [saved, setSaved] = useState("");
  const [versions, setVersions] = useState<ChartVersion[]>([]);
  const [signedIn, setSignedIn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const map = useRef<ChartMap>({ lines: [], instrumentals: [] });

  useEffect(() => {
    supabase.from("songs").select("*").eq("slug", slug).single()
      .then(({ data }) => setRow(data as SongRow | null));
    supabase.auth.getSession().then(({ data }) => setSignedIn(!!data.session));
    const { data: sub } = supabase.auth.onAuthStateChange(
      (_e, session) => setSignedIn(!!session));
    return () => sub.subscription.unsubscribe();
  }, [slug]);

  const load = useCallback(async () => {
    if (!row?.analysis_key) return;
    const plain = { transpose: 0, capo: 0, simple: false };
    const [next, charts] = await Promise.all([
      loadAnalysis(row),
      supabase.from("song_charts").select("id,text,author,created_at")
        .eq("song_id", row.id).order("id", { ascending: false }).limit(20),
    ]);
    if (!next) return;
    setAnalysis(next);

    // The map always comes from the analysis, because it is what knows which
    // beat a word is sung on. Authored spacing lives in the text.
    const fresh = renderChart(buildRows(next, plain), next);
    map.current = fresh.map;

    const stored = (charts.data ?? []) as ChartVersion[];
    setVersions(stored);
    const current = stored[0]?.text ?? fresh.text;
    setLines(linesFromText(current));
    setSaved(current);
  }, [row?.id, row?.analysis_key]);

  useEffect(() => { void load(); }, [row?.analysis_key]);

  const text = useMemo(() => textFromLines(lines), [lines]);
  const dirty = text !== saved;

  // The bar each line starts on, for the gutter. Word slots know their beat;
  // the analysis knows which bar a beat falls in.
  const bars = useMemo(() => lines.map((_, index) => {
    const slot = map.current.lines[index]?.[0];
    if (!slot || !analysis) return null;
    return (analysis.beats[slot.beat]?.bar ?? 0) + 1;
  }), [lines, analysis]);
  const lastBeat = useMemo(
    () => (analysis?.beats.length ? analysis.beats[analysis.beats.length - 1].i : 0),
    [analysis]);

  const willWrite = useMemo(() => {
    if (!analysis) return 0;
    return parseChart(text, map.current).length;
  }, [text, analysis]);

  async function save() {
    if (!row || !analysis) return;
    setBusy(true);
    setProblem(null);
    setNote(null);
    try {
      const parsed = parseChart(text, map.current);
      if (!parsed.length) {
        setProblem("No chords found. Chord lines sit above the words they change on.");
        return;
      }
      // Store the text exactly as typed, then read the chords out of it so the
      // chart follows. Two writes, one truth: the text is the document.
      const { data: written, error } = await supabase.from("song_charts")
        .insert({ song_id: row.id, text })
        .select("id").single();
      if (error) throw error;

      const rows = toCorrections(parsed, lastBeat);
      for (let at = 0; at < rows.length; at += 400) {
        const slice = rows.slice(at, at + 400);
        await correctSpan(row.id, 0, 0, null);   // no-op guard for empty slices
        const { error: failed } = await supabase.from("corrections_log")
          .insert(slice.map((r) => ({ song_id: row.id, beat: r.beat, chord: r.chord })));
        if (failed) throw failed;
      }
      // The words are the person's now, so the timings measured for the
      // machine's transcription no longer describe them. Ask the worker to
      // listen to the same recording again and time what was actually typed;
      // until it answers the chart plays on the old clock rather than none.
      let timing = "";
      if (written?.id) {
        const { error: queued } = await supabase.rpc("request_realign", {
          p_song_id: row.id, p_chart_id: written.id,
        });
        timing = queued
          ? " Timings could not be queued for re-measuring."
          : " Re-timing the words against the recording — a minute or so.";
      }

      setSaved(text);
      setNote(`Saved. ${parsed.length} chords across ${rows.length} beats.${timing}`);
      await load();
    } catch (error) {
      setProblem(error instanceof Error ? error.message : "That did not save.");
    } finally {
      setBusy(false);
    }
  }

  function resetToDetected() {
    if (!analysis) return;
    const fresh = renderChart(
      buildRows(analysis, { transpose: 0, capo: 0, simple: false }), analysis);
    map.current = fresh.map;
    setLines(linesFromText(fresh.text));
  }

  if (!row) return <div className="page"><p className="muted">Loading…</p></div>;
  if (!analysis) {
    return (
      <div className="page">
        <h1 className="serif song-title">{titleOf(row)}</h1>
        <p className="muted">This song has not been worked out yet.</p>
        <p><Link to={`/song/${slug}`}>Back to the song</Link></p>
      </div>
    );
  }

  return (
    <div className="page editor-page">
      <header className="editor-head">
        <div>
          <p className="editor-eyebrow mono">Editing chords</p>
          <h1 className="serif song-title">{titleOf(row)}</h1>
          <p className="muted">
            {artistOf(row)} · {analysis.analysis.key} ·{" "}
            {Math.round(analysis.analysis.bpm)} bpm ·{" "}
            <Link to={`/song/${slug}`}>back to the chart</Link>
          </p>
        </div>
        <div className="editor-count mono">
          <span>{willWrite} chords</span>
          <span>{versions.length} versions</span>
          {dirty && <span className="unsaved">unsaved</span>}
        </div>
      </header>

      {!signedIn && (
        <p className="notice">
          <Link to="/signin">Sign in</Link> to save changes. You can read and
          copy the chart either way.
        </p>
      )}
      {problem && <p className="notice notice-bad">{problem}</p>}
      {note && <p className="notice">{note}</p>}

      <div className="editor-grid">
        <section>
          <div className="editor-toolbar no-print">
            <button className="btn btn-primary" disabled={!signedIn || busy || !dirty}
                    onClick={save}>{busy ? "Saving…" : "Save"}</button>
            <button className="btn" disabled={busy || !dirty}
                    onClick={() => setLines(linesFromText(saved))}>Undo changes</button>
            <button className="btn" disabled={busy}
                    onClick={resetToDetected}
                    title="Throw away edits and start from what the engine heard">
              Start from detected
            </button>
          </div>

          <p className="muted small">
            <kbd>Tab</kbd> to the chord above the caret · <kbd>←</kbd> <kbd>→</kbd>
            nudge it a character · <kbd>Ctrl K</kbd> add one · <kbd>Enter</kbd> split
            a line · <kbd>Backspace</kbd> at the start joins it to the line above,
            chords and all.
          </p>
          <PairedEditor
            lines={lines}
            focus={focus}
            bars={bars}
            disabled={!signedIn}
            onChange={(next, where) => { setLines(next); if (where) setFocus(where); }}
            onFocus={setFocus}
          />
        </section>

        <aside className="editor-history">
          <h2 className="section-heading serif">Versions</h2>
          {versions.length === 0 && (
            <p className="muted small">Nobody has edited this chart yet.</p>
          )}
          <ol className="history-list">
            {versions.map((version, index) => (
              <li key={version.id}>
                <div className="history-line">
                  <span className="mono history-chord">
                    {index === 0 ? "current" : `version ${versions.length - index}`}
                  </span>
                  <span className="muted small">{when(version.created_at)}</span>
                </div>
                <div className="muted small">
                  {version.text.split("\n").length} lines
                  {signedIn && index > 0 && (
                    <button className="btn small-btn history-undo"
                            onClick={() => setLines(linesFromText(version.text))}
                            title="Load this version into the editor">
                      restore
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ol>

        </aside>
      </div>
    </div>
  );
}
